const OPEN_CLASS = 'chrome-blur-backdrop--open';
const ACTIVE_BODY_CLASS = 'chrome-blur-backdrop-active';
const TOP_BAR_SELECTORS = ['.topBar', '.topMemberBar'];
const BOTTOM_BAR_SELECTORS = ['.bottomBar', '.bottomMemberBar'];
const CLOSE_FADE_MS = 150;
const openBackdropIds = new Set();
const pendingCloseById = new Map();
const dismissByElement = new WeakMap();

function bindBackdropDismiss(element, onDismiss) {
    const previous = dismissByElement.get(element);
    if (previous) {
        element.removeEventListener('click', previous);
    }
    element.addEventListener('click', onDismiss);
    dismissByElement.set(element, onDismiss);
}

function unbindBackdropDismiss(element) {
    const previous = dismissByElement.get(element);
    if (!previous) {
        return;
    }
    element.removeEventListener('click', previous);
    dismissByElement.delete(element);
}

function syncActiveBodyClass() {
    document.body.classList.toggle(ACTIVE_BODY_CLASS, openBackdropIds.size > 0);
}

function cancelPendingClose(id) {
    const pending = pendingCloseById.get(id);
    if (!pending) {
        return;
    }

    clearTimeout(pending.timer);
    pendingCloseById.delete(id);
}

function finalizeClose(id, element) {
    pendingCloseById.delete(id);
    openBackdropIds.delete(id);
    if (element) {
        clearChromeInsets(element);
    }
    syncActiveBodyClass();
}

function scheduleClose(id, element) {
    cancelPendingClose(id);
    const timer = setTimeout(() => {
        finalizeClose(id, element);
    }, CLOSE_FADE_MS);
    pendingCloseById.set(id, { timer, element });
}

function isElementVisible(element) {
    if (!element) {
        return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function firstVisibleElement(selectors) {
    for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (isElementVisible(element)) {
            return element;
        }
    }

    return null;
}

let cachedControlBarRadiusPx = null;

function measureControlBarRadiusPx() {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:var(--control-bar-radius);height:0;';
    document.body.appendChild(probe);
    const radiusPx = probe.getBoundingClientRect().width;
    probe.remove();
    return Number.isFinite(radiusPx) ? radiusPx : 0;
}

function readControlBarRadiusPx() {
    if (cachedControlBarRadiusPx === null) {
        cachedControlBarRadiusPx = measureControlBarRadiusPx();
    }
    return cachedControlBarRadiusPx;
}

window.addEventListener('resize', () => {
    cachedControlBarRadiusPx = null;
});

function applyChromeInsets(element) {
    const topBar = firstVisibleElement(TOP_BAR_SELECTORS);
    const bottomBar = firstVisibleElement(BOTTOM_BAR_SELECTORS);
    const radiusPx = readControlBarRadiusPx();

    element.style.top = topBar
        ? `${Math.max(0, Math.round(topBar.getBoundingClientRect().bottom - radiusPx))}px`
        : '0';
    element.style.bottom = bottomBar
        ? `${Math.max(0, Math.round(window.innerHeight - bottomBar.getBoundingClientRect().top - radiusPx))}px`
        : '0';
}

function clearChromeInsets(element) {
    element.style.top = '';
    element.style.bottom = '';
}

export function applyChromeBlurBackdropInsets(element) {
    if (!(element instanceof HTMLElement)) {
        throw new Error('applyChromeBlurBackdropInsets requires an HTMLElement');
    }
    applyChromeInsets(element);
}

export function clearChromeBlurBackdropInsets(element) {
    if (!(element instanceof HTMLElement)) {
        throw new Error('clearChromeBlurBackdropInsets requires an HTMLElement');
    }
    clearChromeInsets(element);
}

export function setChromeBlurBackdropActive(id, isOpen) {
    if (typeof id !== 'string' || id.trim() === '') {
        throw new Error('setChromeBlurBackdropActive requires id');
    }
    if (typeof isOpen !== 'boolean') {
        throw new Error('setChromeBlurBackdropActive requires boolean isOpen');
    }

    const backdropId = id.trim();

    if (isOpen) {
        cancelPendingClose(backdropId);
        openBackdropIds.add(backdropId);
        syncActiveBodyClass();
        return;
    }

    if (pendingCloseById.has(backdropId)) {
        return;
    }

    if (!openBackdropIds.has(backdropId)) {
        return;
    }

    scheduleClose(backdropId, null);
}

function setBackdropOpenState(element, id, isOpen) {
    if (isOpen) {
        cancelPendingClose(id);
        applyChromeInsets(element);
        openBackdropIds.add(id);
        element.hidden = false;
        element.classList.add(OPEN_CLASS);
        syncActiveBodyClass();
        return;
    }

    element.hidden = true;
    element.classList.remove(OPEN_CLASS);

    if (pendingCloseById.has(id)) {
        return;
    }

    if (!openBackdropIds.has(id)) {
        clearChromeInsets(element);
        return;
    }

    scheduleClose(id, element);
}

function requireNonEmptyString(value, name) {
    if (typeof value !== 'string' || value.trim() === '') {
        throw new Error(`createChromeBlurBackdrop requires ${name}`);
    }

    return value.trim();
}

export function createChromeBlurBackdrop(options) {
    if (options === null || typeof options !== 'object') {
        throw new Error('createChromeBlurBackdrop requires options');
    }

    const id = requireNonEmptyString(options.id, 'id');
    const label = requireNonEmptyString(options.label, 'label');

    if (typeof options.onDismiss !== 'function') {
        throw new Error('createChromeBlurBackdrop requires onDismiss');
    }

    const onDismiss = options.onDismiss;
    const mount = options.mount === undefined ? document.body : options.mount;
    if (!(mount instanceof HTMLElement)) {
        throw new Error('createChromeBlurBackdrop requires mount HTMLElement');
    }

    let element = document.getElementById(id);
    if (!element) {
        element = document.createElement('button');
        element.type = 'button';
        element.id = id;
        element.className = 'chrome-blur-backdrop';
        element.setAttribute('aria-label', label);
        element.hidden = true;
        mount.appendChild(element);
    } else {
        if (!(element instanceof HTMLButtonElement)) {
            throw new Error(`createChromeBlurBackdrop element #${id} must be a button`);
        }
        element.classList.add('chrome-blur-backdrop');
        element.setAttribute('aria-label', label);
        if (element.parentElement !== mount) {
            mount.appendChild(element);
        }
    }

    bindBackdropDismiss(element, onDismiss);

    return {
        element,
        setOpen(isOpen) {
            if (typeof isOpen !== 'boolean') {
                throw new Error('chromeBlurBackdrop.setOpen requires boolean');
            }
            setBackdropOpenState(element, id, isOpen);
        },
        destroy() {
            unbindBackdropDismiss(element);
            cancelPendingClose(id);
            openBackdropIds.delete(id);
            syncActiveBodyClass();
            clearChromeInsets(element);
            element.remove();
        },
    };
}
